import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Alert,
  Button,
  Tooltip,
  IconButton,
  Badge,
} from '@mui/material';
import { Search, PersonAdd, OpenInFull, Map as MapIcon, FilterList, FilterAltOff } from '@mui/icons-material';
import {
  useClients,
  useClientsForMap,
  useCustomerCities,
  useCreateCustomer,
  useActivities,
  useActiveReminder,
  useMarkReminderAsSeen,
  CRM_QUERY_KEYS,
} from '../hooks/useClients';
import { useClientListFilters, CLIENT_LIST_PAGE_SIZES } from '../hooks/useClientListFilters';
import { crmService } from '../services/crmService';
import { useQueryClient } from '@tanstack/react-query';
import { CLIENT_LIST_SORTABLE_COLUMNS } from '../models/Client';
import ClientDetailsDrawer from './ClientDetailsDrawer';
import CustomerFormDialog from './CustomerFormDialog';
import ClientsMapPanel from './ClientsMapPanel';
import ClientStatusFilterPopover from './ClientStatusFilterPopover';
import ClientCityFilterPopover from './ClientCityFilterPopover';
import ClientsTableRow from './ClientsTableRow';
import CustomPagination from '../../shared/components/Pagination';

const ClientsList = () => {
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [dismissingClientId, setDismissingClientId] = useState(null);
  const [isListExpanded, setIsListExpanded] = useState(false);

  const filters = useClientListFilters();
  const {
    searchTerm,
    setSearchTerm,
    appliedSearch,
    page,
    pageSize,
    sortConfig,
    selectedStatuses,
    selectedCities,
    filterAnchorEl,
    setFilterAnchorEl,
    cityFilterAnchorEl,
    setCityFilterAnchorEl,
    filterOpen,
    cityFilterOpen,
    allStatusesSelected,
    allCitiesSelected,
    hasStatusFilter,
    hasCityFilter,
    hasActiveFilters,
    listParams,
    mapFilterParams,
    handleSearch,
    handleClearSearch,
    handleSort,
    handleToggleStatus,
    handleSelectAllStatuses,
    handleClearAllStatuses,
    handleToggleCity,
    handleSelectAllCities,
    handleClearAllCities,
    setCityOptionsCount,
    handlePageChange,
    handlePageSizeChange,
    resetSearch,
    statusesParam,
    citiesParam,
  } = filters;

  const clearSelection = () => setSelectedClientId(null);

  const { data: cityOptions = [], isLoading: isCitiesLoading } = useCustomerCities();

  useEffect(() => {
    setCityOptionsCount(cityOptions.length);
  }, [cityOptions.length, setCityOptionsCount]);

  useEffect(() => {
    clearSelection();
  }, [statusesParam, citiesParam, sortConfig.field, sortConfig.direction]);

  const { data, isLoading, isFetching, error, refetch } = useClients(listParams);
  const showMap = !selectedClientId && !isListExpanded;
  const { data: mapClients, isLoading: isMapLoading } = useClientsForMap(mapFilterParams, showMap);

  const createCustomerMutation = useCreateCustomer();
  const markReminderMutation = useMarkReminderAsSeen();
  const queryClient = useQueryClient();
  const { data: selectedActivities } = useActivities(selectedClientId, Boolean(selectedClientId));
  useActiveReminder(selectedClientId, Boolean(selectedClientId));

  const clients = data?.clients || [];
  const emptyListMessage = hasActiveFilters
    ? 'Brak wyników dla wybranych filtrów.'
    : 'Brak klientów do wyświetlenia.';
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const columnCount = isListExpanded ? 10 : 7;
  const showSidePanel = Boolean(selectedClientId) || !isListExpanded;

  const runSearch = () => {
    handleSearch();
    clearSelection();
  };

  const runClearSearch = () => {
    handleClearSearch();
    clearSelection();
  };

  const handleRowClick = (clientId) => {
    setIsListExpanded(false);
    setSelectedClientId(clientId);
  };

  const handlePageChangeWithReset = (newPage) => {
    handlePageChange(newPage);
    clearSelection();
  };

  const handlePageSizeChangeWithReset = (newPageSize) => {
    handlePageSizeChange(newPageSize);
    clearSelection();
  };

  const handleCreateCustomer = async (customerData) => {
    const customerId = await createCustomerMutation.mutateAsync(customerData);
    resetSearch();
    await refetch();
    setSelectedClientId(customerId);
  };

  const handleDismissReminderFromRow = async (event, reminderState) => {
    event.stopPropagation();

    let reminderId = reminderState.reminderId;

    if (!reminderId) {
      try {
        const reminder = await queryClient.fetchQuery({
          queryKey: CRM_QUERY_KEYS.activeReminder(reminderState.id),
          queryFn: () => crmService.getActiveReminder(reminderState.id),
        });
        reminderId = reminder?.id;
      } catch {
        return;
      }
    }

    if (!reminderId) return;

    setDismissingClientId(reminderState.id);
    markReminderMutation.reset();

    try {
      await markReminderMutation.mutateAsync({
        reminderId,
        clientId: reminderState.id,
      });
    } catch {
      // optional: toast
    } finally {
      setDismissingClientId(null);
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Nie udało się załadować listy klientów. Sprawdź połączenie z API.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 200, maxWidth: 560 }}>
          <TextField
            size="small"
            placeholder="Szukaj po nazwie, mieście, adresie lub kodzie pocztowym..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runSearch();
              }
            }}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={runSearch} sx={{ whiteSpace: 'nowrap' }}>
            Szukaj
          </Button>
          <Button
            variant="text"
            onClick={runClearSearch}
            disabled={!searchTerm && !appliedSearch}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Wyczyść
          </Button>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setAddDialogOpen(true)}
        >
          Dodaj klienta
        </Button>
      </Box>

      <ClientStatusFilterPopover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        selectedStatuses={selectedStatuses}
        onToggleStatus={handleToggleStatus}
        onSelectAll={handleSelectAllStatuses}
        onClearAll={handleClearAllStatuses}
        allSelected={allStatusesSelected}
        anySelected={hasStatusFilter}
      />

      <ClientCityFilterPopover
        open={cityFilterOpen}
        anchorEl={cityFilterAnchorEl}
        onClose={() => setCityFilterAnchorEl(null)}
        cities={cityOptions}
        isLoading={isCitiesLoading}
        selectedCities={selectedCities}
        onToggleCity={handleToggleCity}
        onSelectAll={() => handleSelectAllCities(cityOptions)}
        onClearAll={handleClearAllCities}
        allSelected={allCitiesSelected}
        anySelected={hasCityFilter}
      />

      <CustomerFormDialog
        mode="create"
        open={addDialogOpen}
        onClose={() => {
          createCustomerMutation.reset();
          setAddDialogOpen(false);
        }}
        onSubmit={handleCreateCustomer}
        isPending={createCustomerMutation.isPending}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: showSidePanel ? { xs: 2, md: '12px 0 0 12px' } : 2,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {isFetching && (
              <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Tooltip title={isListExpanded ? 'Pokaż mapę' : 'Rozszerz listę'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isListExpanded) {
                      setIsListExpanded(false);
                    } else {
                      clearSelection();
                      setIsListExpanded(true);
                    }
                  }}
                  aria-label={isListExpanded ? 'Pokaż mapę' : 'Rozszerz listę'}
                >
                  {isListExpanded ? <MapIcon fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Table sx={{ opacity: isFetching ? 0.72 : 1, transition: 'opacity 0.2s ease' }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 48, px: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Lp.
                    </Typography>
                  </TableCell>
                  {CLIENT_LIST_SORTABLE_COLUMNS.map(({ field, label }) => {
                    const isFilterableColumn = field === 'city' || field === 'status';

                    return (
                      <TableCell
                        key={field}
                        sortDirection={sortConfig.field === field ? sortConfig.direction : false}
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0,
                            ...(isFilterableColumn ? { ml: '-10px' } : {}),
                          }}
                        >
                          {isFilterableColumn && field === 'status' && (
                            <>
                              <Tooltip title="Filtr statusów">
                                <IconButton
                                  size="small"
                                  onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                                  color={hasStatusFilter ? 'primary' : 'default'}
                                  aria-label="Filtr statusów"
                                  sx={{ p: '2px' }}
                                >
                                  <Badge color="primary" variant="dot" invisible={!hasStatusFilter}>
                                    <FilterList fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              {hasStatusFilter && (
                                <Tooltip title="Wyczyść filtr statusów">
                                  <IconButton
                                    size="small"
                                    onClick={handleClearAllStatuses}
                                    aria-label="Wyczyść filtr statusów"
                                    sx={{ p: '2px' }}
                                  >
                                    <FilterAltOff fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          {isFilterableColumn && field === 'city' && (
                            <>
                              <Tooltip title="Filtr miast">
                                <IconButton
                                  size="small"
                                  onClick={(event) => setCityFilterAnchorEl(event.currentTarget)}
                                  color={hasCityFilter ? 'primary' : 'default'}
                                  aria-label="Filtr miast"
                                  sx={{ p: '2px' }}
                                >
                                  <Badge color="primary" variant="dot" invisible={!hasCityFilter}>
                                    <FilterList fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              {hasCityFilter && (
                                <Tooltip title="Wyczyść filtr miast">
                                  <IconButton
                                    size="small"
                                    onClick={handleClearAllCities}
                                    aria-label="Wyczyść filtr miast"
                                    sx={{ p: '2px' }}
                                  >
                                    <FilterAltOff fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          <TableSortLabel
                            active={sortConfig.field === field}
                            direction={sortConfig.field === field ? sortConfig.direction : 'asc'}
                            onClick={() => {
                              handleSort(field);
                              clearSelection();
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {label}
                            </Typography>
                          </TableSortLabel>
                        </Box>
                      </TableCell>
                    );
                  })}
                  {isListExpanded && (
                    <>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Telefon</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notatka</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ostatni komentarz</Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" sx={{ width: 52, px: 0.5 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {emptyListMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client, index) => (
                    <ClientsTableRow
                      key={client.id}
                      client={client}
                      index={index}
                      page={page}
                      pageSize={pageSize}
                      isSelected={selectedClientId === client.id}
                      isListExpanded={isListExpanded}
                      selectedClientId={selectedClientId}
                      selectedActivities={selectedActivities}
                      onRowClick={handleRowClick}
                      onDismissReminder={handleDismissReminderFromRow}
                      isDismissing={dismissingClientId === client.id && markReminderMutation.isPending}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <CustomPagination
              currentPage={page + 1}
              totalPages={Math.max(totalPages, 1)}
              totalItems={pagination?.totalElements || 0}
              pageSize={pageSize}
              pageSizeOptions={CLIENT_LIST_PAGE_SIZES}
              itemsLabel="klientów"
              onPageChange={handlePageChangeWithReset}
              onPageSizeChange={handlePageSizeChangeWithReset}
              showPageSizeSelector
              showTotalItems
            />
          </Box>

          {showMap && (
            <Box sx={{ mt: 2, display: { xs: 'block', md: 'none' } }}>
              <ClientsMapPanel
                clients={mapClients}
                isLoading={isMapLoading}
                onClientSelect={handleRowClick}
                variant="full"
              />
            </Box>
          )}
        </Box>

        <ClientDetailsDrawer
          clientId={selectedClientId}
          open={Boolean(selectedClientId)}
          onClose={clearSelection}
        />

        {showMap && (
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <ClientsMapPanel
              clients={mapClients}
              isLoading={isMapLoading}
              onClientSelect={handleRowClick}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClientsList;
